<template>
  <Dialog
    v-model="dialogVisible"
    title="Edit Patient Price"
    dialog-type="primary"
    dialog-size="small"
    :close-on-overlay="false"
    @close="handleClose"
  >
    <template #header-icon>
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
      </svg>
    </template>

    <!-- Dialog Content -->
    <div class="edit-retail-body" :class="{ 'is-saving': loading || saving }">
      <!-- Choose Provider Section (Clinic Only) -->
      <div v-if="isClinic" class="section provider-section">
        <div class="section-header">
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="20" viewBox="0 0 18 20" fill="none">
            <path d="M8.99951 0C6.75462 0 4.92874 1.75323 4.92874 3.9082V5.93294C4.92874 8.08807 6.75462 9.8413 8.99951 9.8413C11.2446 9.8413 13.0703 8.08807 13.0703 5.93294V3.9082C13.0703 1.75323 11.2446 0 8.99951 0Z" fill="#374957"/>
            <path d="M17.9775 14.9346C16.674 12.8021 14.5904 11.2212 12.1107 10.483C12.0532 10.4659 11.991 10.4804 11.9482 10.5211C10.7947 11.6129 9.36128 12.4647 9.00294 12.6705C8.62043 12.4358 7.00922 11.4276 6.05136 10.5211C6.00869 10.4804 5.94603 10.4659 5.88886 10.483C3.4088 11.2214 1.32541 12.8023 0.0223756 14.9347C-0.00745852 14.9835 -0.00745852 15.0442 0.0223756 15.0929C1.87276 18.1197 5.31268 20 8.99977 20C12.687 20 16.1271 18.1197 17.9775 15.0929C18.0075 15.044 18.0075 14.9834 17.9775 14.9346ZM14.5364 16.1003C14.5364 16.1865 14.4509 16.2524 14.3612 16.2524H13.2587C13.1689 16.2524 13.0709 16.3265 13.0709 16.4129V17.5074C13.0709 17.5935 13.0229 17.6593 12.933 17.6593H11.9428C11.853 17.6593 11.7683 17.5935 11.7683 17.5074V16.4129C11.768 16.3267 11.7067 16.2524 11.6168 16.2524H10.4668C10.377 16.2524 10.3026 16.1864 10.3026 16.1003V15.1579C10.3026 15.0717 10.377 15.0021 10.4668 15.0021H11.6168C11.7067 15.0021 11.768 14.9317 11.768 14.8453V13.7495C11.768 13.6632 11.8527 13.5952 11.9425 13.5952H12.922C13.0117 13.5952 13.0707 13.6632 13.0707 13.7495V14.8485C13.0707 14.9349 13.1577 15.0019 13.2477 15.0019H14.3612C14.4509 15.0019 14.5364 15.0749 14.5364 15.1611V16.1003Z" fill="#374957"/>
          </svg>
          <span class="section-title">Choose Provider</span>
        </div>
        
        <div class="form-field">
          <label class="field-label">
            Edit Patient Price for <span class="required">(Required)</span>
          </label>
          <MultiSelector
            v-model="selectedProviderValues"
            size="medium"
            placeholder="Search providers..."
            :options="providerOptions"
          />
        </div>

        <!-- Allow Customization Toggle - all on same row -->
        <div class="form-field customization-field">
          <div class="customization-row">
            <label class="field-label customization-label">Allow Customization</label>
            <div class="toggle-group">
              <span class="toggle-label" :class="{ active: !allowCustomization }">Do not Allow Customization</span>
              <div class="custom-toggle" :class="{ 'active': allowCustomization }" @click="allowCustomization = !allowCustomization">
                <div class="custom-toggle-track">
                  <div class="custom-toggle-thumb"></div>
                </div>
              </div>
              <span class="toggle-label" :class="{ active: allowCustomization }">Allow Customization</span>
            </div>
          </div>
        </div>

        <!-- Info Notification -->
        <InlineNotification
          v-if="showCustomizationInfo"
          theme="info"
          class="info-alert"
          message="If customization is not allowed, providers cannot change this price on their own."
          :with_close_btn="true"
          @close="showCustomizationInfo = false"
        />
      </div>

      <!-- Divider -->
      <div v-if="isClinic" class="section-divider"></div>

      <!-- Product Info Section -->
      <div class="section product-section">
        <div class="product-header">
          <div class="product-name">
            <div class="product-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="18" viewBox="0 0 20 18" fill="none">
                <path d="M0 16.1914V4.82303C0 3.82414 0.790087 3.01439 1.76471 3.01439H2.54902C2.87389 3.01439 3.13725 3.28431 3.13725 3.61727C3.13725 3.95023 2.87389 4.22015 2.54902 4.22015H1.76471C1.43983 4.22015 1.17647 4.49007 1.17647 4.82303V16.1914C1.17647 16.5243 1.43983 16.7942 1.76471 16.7942H18.2353C18.5602 16.7942 18.8235 16.5243 18.8235 16.1914V4.82303C18.8235 4.49007 18.5602 4.22015 18.2353 4.22015H17.451C17.1261 4.22015 16.8627 3.95023 16.8627 3.61727C16.8627 3.28431 17.1261 3.01439 17.451 3.01439H18.2353C19.2099 3.01439 20 3.82415 20 4.82303V16.1914C20 17.1902 19.2099 18 18.2353 18H1.76471C0.790086 18 0 17.1902 0 16.1914Z" fill="#374957"/>
                <path d="M9.60784 0V1.36652H8.98039V10.9322C8.98039 11.657 8.71597 12.3522 8.24529 12.8648C7.77461 13.3773 7.13623 13.6652 6.47059 13.6652C5.80495 13.6652 5.16657 13.3773 4.69589 12.8648C4.22521 12.3522 3.96078 11.657 3.96078 10.9322V1.36652H3.33333V0H9.60784ZM7.72549 5.4661H5.21569V10.9322C5.21569 11.2946 5.3479 11.6422 5.58324 11.8985C5.81858 12.1547 6.13777 12.2987 6.47059 12.2987C6.80341 12.2987 7.1226 12.1547 7.35794 11.8985C7.59328 11.6422 7.72549 11.2946 7.72549 10.9322V5.4661ZM7.09804 8.88241C7.26445 8.88241 7.42404 8.95439 7.54171 9.08253C7.65938 9.21067 7.72549 9.38446 7.72549 9.56567C7.72549 9.74688 7.65938 9.92067 7.54171 10.0488C7.42404 10.1769 7.26445 10.2489 7.09804 10.2489C6.93163 10.2489 6.77203 10.1769 6.65436 10.0488C6.5367 9.92067 6.47059 9.74688 6.47059 9.56567C6.47059 9.38446 6.5367 9.21067 6.65436 9.08253C6.77203 8.95439 6.93163 8.88241 7.09804 8.88241ZM5.84314 6.83262C6.00955 6.83262 6.16914 6.90461 6.28681 7.03274C6.40448 7.16088 6.47059 7.33467 6.47059 7.51588C6.47059 7.6971 6.40448 7.87089 6.28681 7.99902C6.16914 8.12716 6.00955 8.19915 5.84314 8.19915C5.67673 8.19915 5.51713 8.12716 5.39946 7.99902C5.28179 7.87089 5.21569 7.6971 5.21569 7.51588C5.21569 7.33467 5.28179 7.16088 5.39946 7.03274C5.51713 6.90461 5.67673 6.83262 5.84314 6.83262ZM7.72549 1.36652H5.21569V4.09957H7.72549V1.36652Z" fill="#374957"/>
                <path d="M10.3922 0V1.36652H11.0196V10.9322C11.0196 11.657 11.284 12.3522 11.7547 12.8648C12.2254 13.3773 12.8638 13.6652 13.5294 13.6652C14.1951 13.6652 14.8334 13.3773 15.3041 12.8648C15.7748 12.3522 16.0392 11.657 16.0392 10.9322V1.36652H16.6667V0H10.3922ZM12.902 9.56567C12.5255 9.56567 12.2745 9.29237 12.2745 8.88241C12.2745 8.47245 12.5255 8.19915 12.902 8.19915C13.2784 8.19915 13.5294 8.47245 13.5294 8.88241C13.5294 9.29237 13.2784 9.56567 12.902 9.56567ZM14.1569 6.83262C13.7804 6.83262 13.5294 6.55932 13.5294 6.14936C13.5294 5.7394 13.7804 5.4661 14.1569 5.4661C14.5333 5.4661 14.7843 5.7394 14.7843 6.14936C14.7843 6.55932 14.5333 6.83262 14.1569 6.83262ZM14.7843 3.41631H12.2745V1.36652H14.7843V3.41631Z" fill="#374957"/>
              </svg>
            </div>
            <span>{{ selectedProduct?.name || 'TEST NAME' }}</span>
          </div>
          <div class="current-price">
            Current Price: <strong>{{ currencySymbol }}{{ formatPrice(selectedProduct?.basePrice || 0) }}</strong>
          </div>
        </div>

        <!-- Customized Name -->
        <div class="form-field">
          <label class="field-label">Customized Name</label>
          <TextInput
            v-model="customizedName"
            size="medium"
            placeholder="Customized test name your patients will see ..."
            :disabled="priceUpdateType === 'no_markup'"
            :clearable="false"
          />
        </div>

        <!-- Warning Notification -->
        <InlineNotification
          v-if="showNameWarning"
          theme="warning"
          class="warning-alert name-warning"
          message="To prevent potential confusion in the materials available to the patient, it is advisable to assign a different name to this item if its current name could lead to any misunderstandings."
          :with_close_btn="true"
          @close="showNameWarning = false"
        />

        <!-- Price Update Type -->
        <div class="form-field price-update-row">
          <div class="price-type-field">
            <label class="field-label">Price Update Type</label>
            <SingleSelector
              v-model="priceUpdateType"
              size="medium"
              :options="priceUpdateOptions"
              placeholder="Select price update type"
              @change="handlePriceTypeChange"
            />
          </div>
          
          <!-- Dynamic Input Field based on Price Update Type -->
          <div v-if="priceUpdateType !== 'no_markup'" class="price-value-field">
            <label class="field-label">{{ priceInputLabel }}</label>
            <TextInput
              v-model="priceValue"
              size="medium"
              :placeholder="priceInputPlaceholder"
              :clearable="true"
              :class="{ 'price-input-error': priceError }"
            >
              <template v-if="currentPrefix" #prefix>
                <span class="price-input-prefix">{{ currentPrefix }}</span>
              </template>
              <template v-if="priceUpdateType === 'added_percentage'" #suffix>
                <span class="price-input-suffix">%</span>
              </template>
            </TextInput>
          </div>
        </div>

        <!-- Price Error Notification -->
        <InlineNotification
          v-if="priceError"
          theme="error"
          class="error-alert"
          message="The retail price must be set between 100% and 150% of the wholesale price."
          :with_close_btn="false"
        />

        <!-- Fixed Price Warning -->
        <InlineNotification
          v-if="priceUpdateType === 'fixed_price' && showFixedPriceWarning"
          theme="warning"
          class="warning-alert"
          message="In future when Vibrant adjusts its Price, and the new price is higher than your flat rate, your flat rate will be set to match the Wholesale Price. Should that happen, an email will be sent to you."
          :with_close_btn="true"
          @close="showFixedPriceWarning = false"
        />

        <!-- Retail Price Display -->
        <div class="price-display">
          <div class="price-row">
            <span class="price-label">Total Patient Price</span>
            <span class="price-value">{{ currencySymbol }}{{ formatPrice(calculatedRetailPrice) }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Dialog Footer -->
    <template #footer>
      <ResponsiveButton
        button_type="outlined"
        built_in_theme="error"
        size="medium"
        display_name="Cancel"
        @click-button="handleClose"
      />
      <ResponsiveButton
        button_type="filled"
        built_in_theme="dark"
        size="medium"
        display_name="Save"
        :disabled="!canSave || saving"
        @click-button="handleSave"
      />
    </template>
  </Dialog>
</template>

<script>
import { postMarkUps } from '@/api/markup';
import { notify } from '@/utils/notify';

export default {
  name: 'EditRetailDetailsDialog',

  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    selectedProduct: {
      type: Object,
      default: null,
    },
    providerOptions: {
      type: Array,
      default: () => [],
    },
    currentProviderId: {
      type: [Number, String],
      default: null,
    },
    isClinic: {
      type: Boolean,
      default: false,
    },
    clinicId: {
      type: [Number, String],
      default: null,
    },
    customerId: {
      type: [Number, String],
      default: null,
    },
    currencySymbol: {
      type: String,
      default: '$',
    },
  },

  emits: ['update:modelValue', 'save', 'updateTableRow'],

  data() {
    return {
      loading: false,
      saving: false,

      // Provider selection — array of provider values, bound to MultiSelector v-model
      selectedProviderValues: [],
      
      // Customization
      allowCustomization: true,
      showCustomizationInfo: true,
      
      // Product details
      customizedName: '',
      showNameWarning: true,
      showFixedPriceWarning: true,
      
      // Price update
      priceUpdateType: 'no_markup',
      priceValue: '',
      priceError: false,
      
      // Options
      priceUpdateOptions: [
        { value: 'no_markup', label: 'No Price Update' },
        { value: 'fixed_profit', label: 'Price Update by Revenue' },
        { value: 'added_percentage', label: 'Price Update by Percentage' },
        { value: 'fixed_price', label: 'Fixed Patient Price' },
      ],
    };
  },

  computed: {
    dialogVisible: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit('update:modelValue', value);
      },
    },

    priceInputLabel() {
      switch (this.priceUpdateType) {
        case 'fixed_profit':
          return 'Amount';
        case 'added_percentage':
          return 'Percentage';
        case 'fixed_price':
          return 'Updated Price';
        default:
          return '';
      }
    },

    priceInputPlaceholder() {
      switch (this.priceUpdateType) {
        case 'fixed_profit':
          return `e.g. ${this.currencySymbol}5`;
        case 'added_percentage':
          return 'e.g. 10%';
        case 'fixed_price':
          return 'Must be bigger than current';
        default:
          return '';
      }
    },

    currentPrefix() {
      switch (this.priceUpdateType) {
        case 'fixed_profit':
          return `+${this.currencySymbol}`;
        case 'fixed_price':
          return this.currencySymbol;
        case 'added_percentage':
          return '+';
        default:
          return '';
      }
    },

    wholesalePrice() {
      return this.selectedProduct?.basePrice || 0;
    },

    calculatedRetailPrice() {
      if (!this.selectedProduct) return 0;
      
      const wholesale = this.wholesalePrice;
      const value = parseFloat(this.priceValue) || 0;

      switch (this.priceUpdateType) {
        case 'no_markup':
          return wholesale;
        case 'added_percentage':
          return wholesale * (1 + value / 100);
        case 'fixed_profit':
          return wholesale + value;
        case 'fixed_price':
          return value || wholesale;
        default:
          return wholesale;
      }
    },

    calculatedProfit() {
      return this.calculatedRetailPrice - this.wholesalePrice;
    },

    isPriceValid() {
      if (this.priceUpdateType === 'no_markup') return true;
      if (!this.priceValue) return true;

      const value = parseFloat(this.priceValue);
      if (isNaN(value) || value < 0) return false;

      // For all types, validate retail is within range
      const retail = this.calculatedRetailPrice;
      const wholesale = this.wholesalePrice;
      const minPrice = wholesale;
      const maxPrice = wholesale * 1.5;

      return retail >= minPrice && retail <= maxPrice;
    },

    canSave() {
      // Provider is required
      if (this.isClinic) {
        // Clinic: must select at least one provider from the MultiSelector
        if (this.selectedProviderValues.length === 0) {
          return false;
        }
      } else {
        // Non-clinic: must have a provider ID passed in
        if (!this.currentProviderId && !this.customerId) {
          return false;
        }
      }
      // Same logic as VA Portal: only disabled when markup type requires value but no value entered
      if (this.priceUpdateType !== 'no_markup' && !this.priceValue) {
        return false;
      }
      return true;
    },
  },

  watch: {
    modelValue(newVal) {
      if (newVal) {
        this.initializeDialog();
      }
    },

    priceValue(newVal) {
      // Sanitize: only digits and decimal point
      if (typeof newVal === 'string') {
        const sanitized = newVal.replace(/[^\d.]/g, '');
        if (sanitized !== newVal) {
          this.priceValue = sanitized;
          return;
        }
      }
      this.validatePrice();
    },

    priceUpdateType() {
      this.validatePrice();
    },
  },

  methods: {
    initializeDialog() {
      // Reset state
      this.saving = false;
      this.priceError = false;
      this.showCustomizationInfo = true;
      this.showNameWarning = true;
      this.showFixedPriceWarning = true;

      // Initialize with current product data
      if (this.selectedProduct) {
        this.customizedName = this.selectedProduct.customized_name || '';
        this.priceUpdateType = this.selectedProduct.markUpType || 'no_markup';
        this.allowCustomization = this.selectedProduct.changable !== false;

        // Set price value based on markup type
        if (this.priceUpdateType === 'added_percentage') {
          this.priceValue = this.selectedProduct.markUpValue 
            ? String(Math.round(this.selectedProduct.markUpValue * 100))
            : '';
        } else if (this.priceUpdateType !== 'no_markup') {
          this.priceValue = this.selectedProduct.markUpValue 
            ? String(this.selectedProduct.markUpValue)
            : '';
        } else {
          this.priceValue = '';
        }
      }

      // Pre-select current provider into MultiSelector (v-model is array of values)
      const preselectOption = this.currentProviderId != null
        ? this.providerOptions.find(opt => String(opt.value) === String(this.currentProviderId))
        : null;
      this.selectedProviderValues = preselectOption ? [preselectOption.value] : [];
    },

    handlePriceTypeChange() {
      this.priceValue = '';
      this.priceError = false;
    },

    validatePrice() {
      if (this.priceUpdateType === 'no_markup' || !this.priceValue) {
        this.priceError = false;
        return;
      }
      this.priceError = !this.isPriceValid;
    },

    formatPrice(value) {
      return Number(value).toFixed(2);
    },

    handleClose() {
      this.dialogVisible = false;
    },

    async handleSave() {
      if (!this.canSave) return;

      // Same as VA Portal: validate price eligibility on click
      if (this.priceUpdateType !== 'no_markup' && !this.isPriceValid) {
        this.priceError = true;
        return;
      }

      // Validate provider selection on click
      if (this.isClinic && this.selectedProviderValues.length === 0) {
        notify({ type: 'error', message: 'Please select at least one provider.' });
        return;
      }

      this.saving = true;

      try {
        // Determine affiliate_ids
        const affiliateIds = this.isClinic
          ? this.selectedProviderValues.map(v => Number(v))
          : [Number(this.currentProviderId || this.customerId)];

        // Determine item_type based on product type
        const itemType = this.getItemType(this.selectedProduct.type);

        // Format markup value (percentage needs to be divided by 100)
        const markupValue = this.priceUpdateType === 'added_percentage'
          ? parseFloat(this.priceValue) / 100
          : parseFloat(this.priceValue) || 0;

        // Build markup_schemes payload
        // Expand ids array for VW deduped items (same as VA Portal formatDataForPostApi)
        const ids = Array.isArray(this.selectedProduct.ids) && this.selectedProduct.ids.length
          ? this.selectedProduct.ids
          : [this.selectedProduct.id];
        const markupSchemes = {};
        affiliateIds.forEach(affId => {
          markupSchemes[affId] = ids.map(idVal => ({
            item_type: itemType,
            item_id: String(idVal),
            item_name: this.selectedProduct.name,
            markup_type: this.priceUpdateType,
            markup_value: markupValue,
            changeable: this.allowCustomization,
            customized_name: this.customizedName || null,
          }));
        });

        const payload = {
          account_type: this.isClinic ? 'clinic' : 'customer',
          account_id: this.isClinic ? Number(this.clinicId) : Number(this.customerId),
          clinic_id: Number(this.clinicId),
          customer_id: Number(this.customerId),
          markup_schemes: markupSchemes,
        };

        await postMarkUps(payload);

        // Emit update event for parent to update table
        this.$emit('updateTableRow', {
          product: this.selectedProduct,
          affiliateIds,
          markUpType: this.priceUpdateType,
          markUpValue: markupValue,
          customizedName: this.customizedName,
          changable: this.allowCustomization,
        });

        notify({
          type: 'success',
          title: 'Success',
          message: 'Patient price updated successfully',
        });

        this.handleClose();
      } catch (error) {
        console.error('Failed to update retail price:', error);
        notify({
          type: 'error',
          title: 'Error',
          message: 'Failed to update patient price. Please try again.',
        });
      } finally {
        this.saving = false;
      }
    },

    getItemType(productType) {
      switch (productType) {
        case 'va':
        case 'vw':
          return 'packagePriceId';
        case 'promotion':
          return 'oldPromotionId';
        case 'favorites':
          return 'shortcutId';
        case 'discount':
        case 'custom':
        default:
          return 'bundleId';
      }
    },
  },
};
</script>

<style scoped>
.dialog-content {
  padding: 24px;
}

.section {
  margin-bottom: 12px;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.section-title {
  color: var(--TrueBlack, #333);
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0.15px;
}

.section-divider {
  height: 1px;
  background: #E6E6E6;
  margin: 12px 0;
}

.form-field {
  margin-bottom: 12px;
}

.field-label {
  display: block;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #4A4A4A;
  margin-bottom: 6px;
}

.customization-row .field-label {
  display: inline-block;
  margin-bottom: 0;
  margin-right: 8px;
}

.required {
  color: #D10000;
  font-weight: 500;
}

:deep(.combo-box-suffix) {
  display: flex;
  align-items: center;
}

.customization-field {
  margin-top: 12px;
}

.customization-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.toggle-group {
  display: flex;
  align-items: center;
  gap: 12px;
}

.customization-label {
  color: var(--TrueBlack, #333);
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.1px;
}

.toggle-wrapper {
  display: flex;
  align-items: center;
  gap: 12px;
}

.toggle-label {
  color: var(--SecondaryBlack, #666);
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0.4px;
}

.info-alert, .warning-alert, .error-alert {
  margin-top: 8px;
}

.name-warning {
  margin-bottom: 16px;
}

/* Custom toggle - same style as PracticeProductCard but bigger */
.custom-toggle {
  width: 44px;
  height: 24px;
  cursor: pointer;
  flex-shrink: 0;
}

.custom-toggle-track {
  width: 100%;
  height: 100%;
  border-radius: 12px;
  background: #E3E5E8;
  position: relative;
  transition: background 0.2s;
}

.custom-toggle.active .custom-toggle-track {
  background: var(--IconGrey, #374957);
}

.custom-toggle-thumb {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #FFF;
  position: absolute;
  top: 2px;
  left: 2px;
  transition: left 0.2s;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
}

.custom-toggle.active .custom-toggle-thumb {
  left: 22px;
  background: linear-gradient(90deg, #E6EDF2 0%, #EAF8F9 100%);
}

.alert-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.alert-content span {
  color: var(--TrueBlack, #333);
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 300;
  line-height: 18px;
}

.alert-close {
  background: none;
  border: none;
  font-size: 18px;
  cursor: pointer;
  color: inherit;
  opacity: 0.7;
  flex-shrink: 0;
}

.alert-close:hover {
  opacity: 1;
}

.product-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  margin-bottom: 12px;
}

.product-icon {
  display: flex;
  width: 24px;
  height: 24px;
  padding: 2px;
  justify-content: center;
  align-items: center;
  gap: 10px;
}

.product-name {
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.current-price {
  color: var(--IconGrey, #374957);
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 28px;
}

.current-price strong {
  color: var(--TrueBlack, #333);
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0.15px;
}

.price-update-row {
  display: flex;
  gap: 16px;
}

.price-type-field {
  flex: 1;
}

.price-type-field :deep(.single-selector-unit-wrapper) {
  width: 100%;
}

.price-value-field {
  flex: 1;
}

/* Prefix/suffix text inside the new TextInput */
.price-input-prefix,
.price-input-suffix {
  color: var(--IconGrey, #374957);
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 20px;
}

/* Error border for the new TextInput */
.price-input-error :deep(.text-input) {
  border-color: #D10000 !important;
}

.price-display {
  margin-top: 0;
  padding-top: 0;
}

.price-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
}

.price-label {
  color: var(--IconGrey, #374957);
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 28px;
}

.price-value {
  color: var(--IconGrey, #374957);
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 28px;
}

.edit-retail-body {
  display: flex;
  flex-direction: column;
  padding: 12px 0;
  transition: opacity 0.2s ease;
}

.edit-retail-body.is-saving {
  opacity: 0.5;
  pointer-events: none;
}
</style>
